/**
 * TimeSlicer —— 周期切换 + 日期范围 (Phase 5-1 共享组件)
 *
 * 提供两个 input 集合：
 *  1. 周期 Radio: day / week / month / quarter / year（受控）
 *  2. 日期范围 RangePicker（受控）—— onChange 回调拿 ISO 字符串
 *
 * 受控模式：caller 通过 value + onChange 管理状态。
 */
import { Radio, DatePicker, Space } from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { Dayjs } from 'dayjs';
import type { Period } from '../../api/dashboard';

const { RangePicker } = DatePicker;

export interface TimeSlicerValue {
  period?: Period;
  startAt: string;
  endAt: string;
}

export interface TimeSlicerProps {
  value: TimeSlicerValue;
  onChange: (next: TimeSlicerValue) => void;
  /** 是否显示 period 切换（D4/D6 不需要 period） */
  showPeriod?: boolean;
  testId?: string;
}

const PERIODS: { value: Period; label: string }[] = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'quarter', label: '季' },
  { value: 'year', label: '年' },
];

export function TimeSlicer({
  value,
  onChange,
  showPeriod = true,
  testId,
}: TimeSlicerProps) {
  const onPeriodChange = (e: RadioChangeEvent) => {
    onChange({ ...value, period: e.target.value as Period });
  };
  const onRangeChange = (
    range: [Dayjs | null, Dayjs | null] | null,
  ) => {
    if (!range || !range[0] || !range[1]) return;
    onChange({
      ...value,
      startAt: range[0].toISOString(),
      endAt: range[1].toISOString(),
    });
  };

  const baseId = testId ?? 'time-slicer';
  return (
    <Space wrap data-testid={baseId}>
      {showPeriod && (
        <div data-testid={`${baseId}-period`}>
          <Radio.Group
            value={value.period ?? 'day'}
            onChange={onPeriodChange}
            options={PERIODS}
            optionType="button"
          />
        </div>
      )}
      <div data-testid={`${baseId}-range`}>
        <RangePicker
          showTime={false}
          onChange={(r) => onRangeChange(r as [Dayjs | null, Dayjs | null] | null)}
        />
      </div>
    </Space>
  );
}
